import { AnswerModel } from '@/components/decision-tree/hooks';
import useOnMount from '@/hooks/useOnMount';
import authService from '@/services/auth.service';
import { ChangeEvent } from 'react';
import { useForm } from 'react-hook-form';
import { EmailAnswer, EmailValidation } from './types';

type EmailControllerProps = {
  validationValues?: EmailValidation;
  saveAnswer: (payload: AnswerModel) => void;
  defaultValue?: EmailAnswer;
};

export default function useEmailController({ validationValues, saveAnswer, defaultValue }: EmailControllerProps) {
  const form = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      email: defaultValue?.value || authService.getVisitorEmail() || '',
    },
  });

  // If authService has a visitor email, save it to the form
  useOnMount(() => {
    if (authService.getVisitorEmail()) {
      saveAnswer({ type: 'email', value: authService.getVisitorEmail() ?? '' });
    }
  });

  const validationOptions = {
    required: 'Email is required',
    pattern: {
      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: 'Please enter a valid email address',
    },
  };

  const onInputFn = ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
    // Update form value
    form.setValue('email', currentTarget.value);

    // Only save if we have a valid email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (currentTarget.value && emailRegex.test(currentTarget.value)) {
      saveAnswer({ type: 'email', value: currentTarget.value });
    }
  };

  const onBlurFn = ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
    // Update form value
    form.setValue('email', currentTarget.value);

    // Save the answer directly since we know the value
    saveAnswer({ type: 'email', value: currentTarget.value });
  };

  return { onInputFn, form, validationOptions, onBlurFn };
}
